import { useState, useEffect, useRef, useCallback } from "react";
import {
  Draggable,
  Droppable,
  DragDropContext,
  DropResult,
} from "react-beautiful-dnd";
import { Link, useParams, useNavigate, useLocation } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import clsx from "clsx";

import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";

import { AppDispatch } from "../../../redux/reducer/store";
import {
  actionGetDesktop,
  actionAddDesktop,
} from "../../../redux/action/charts/actionCharts";
import { reducerEditOrderDesktop } from "../../../redux/reducer/charts/reducer/reducerCharts";

import CustomerCharts from "../../../pages/charts/managementCharts.tsx/customerCharts";
import SectionContent from "../../../components/sectionContent/sectionContent";
import Sidebar from "../../../components/sidebar/Sidebar";
import HeaderBtn from "../../../components/headerBtn/headerBtn";

import { IUserOptions } from "../../../types/interfaces";

import "../../../scss/App.scss";

const ManagementChartsSection = () => {
  const { isOpenSidebar, data } = useSelector(
    (state: IUserOptions) => state.user.user
  );
  const { id } = useParams<{ id: string }>();
  const IS_VISIBLE_SIDEBAR = process.env.REACT_APP_IS_VISIBLE_SIDEBAR;
  const dispatch: any = useDispatch<AppDispatch>();

  const buttonsContainerRef = useRef<HTMLDivElement>(null);

  const headFilterRef = useRef<any>(null);

  const [overflowedBtnsCount, setOverflowedBtnsCount] = useState<number>(0);
  const [isOpenGroupBtn, setIsOpenGroupBtn] = useState<boolean>(false);

  const sidebarWidth = isOpenSidebar ? 190 : 44;
  const headerFiltersAndSelectWidth = 365;
  const minEmptyWidthForNewBtn = 355;
  const groupBtnWidth = 70;
  const buttonWidth = 135;

  const navigate = useNavigate();
  const location = useLocation();

  const { desktopList, defaultDesktop } = useSelector(
    (state: any) => state.charts?.chartsList
  );

  const reviewStatus = data.isAdmin || data.settings.reviewStatus;

  const [desktopListOrder, setDesktopListOrder] = useState<any[]>([]);
  const index =
    desktopList?.length > 0 && defaultDesktop
      ? desktopList.findIndex(
          (el: any) => el.id.toString() === (id || defaultDesktop)
        )
      : 0;

  const findItem =
    desktopList?.length > 0 && desktopList[index === -1 ? 0 : index];

  const availableButtons = [
    desktopList?.length && {
      component: (
        <HeaderBtn
          content="Настройки"
          purpose="chartsSettings"
          modalTitle="Настройки"
        />
      ),
    },
    desktopList?.length && {
      component: (
        <HeaderBtn
          content="Редактировать рабочий стол"
          purpose="editDesktop"
          modalTitle="Редактировать рабочий стол"
        />
      ),
    },
    desktopList?.length && {
      component: (
        <HeaderBtn
          content="Добавить диаграмму"
          purpose="addChart"
          modalTitle="Добавить диаграмму"
        />
      ),
    },
  ];

  const filteredAvailableButtons = availableButtons.filter(
    (btn: { component: JSX.Element }) => btn
  );

  const displayedAvailableBtns = filteredAvailableButtons
    ?.slice(-(overflowedBtnsCount as number))
    ?.map((elem: { component: JSX.Element }, index: number) => {
      return elem && <div key={index}>{elem?.component}</div>;
    });

  const displayedHeadBtns = filteredAvailableButtons
    ?.slice(0, filteredAvailableButtons.length - overflowedBtnsCount)
    ?.map((elem: { component: JSX.Element }, index: number) => {
      return elem && <div key={index}>{elem?.component}</div>;
    });

  useEffect(() => {
    if (
      filteredAvailableButtons.slice(-(overflowedBtnsCount as number)).length +
        filteredAvailableButtons.slice(
          0,
          filteredAvailableButtons.length - overflowedBtnsCount
        ).length >
      filteredAvailableButtons.length
    ) {
      setOverflowedBtnsCount(0);
    }
  }, [filteredAvailableButtons, overflowedBtnsCount]);

  const handleWindowResize = useCallback(() => {
    const allButtonsWidth =
      filteredAvailableButtons.slice(
        0,
        filteredAvailableButtons.length - overflowedBtnsCount
      ).length * buttonWidth;
    const spaceToLeftSideOfFilters =
      headFilterRef.current?.getBoundingClientRect().left;
    const windowWidth = window.innerWidth;

    const isOverflowed = JSON.parse(IS_VISIBLE_SIDEBAR as string)
      ? allButtonsWidth >
        windowWidth - headerFiltersAndSelectWidth - sidebarWidth
      : allButtonsWidth > windowWidth - headerFiltersAndSelectWidth;

    if (isOverflowed) {
      setIsOpenGroupBtn(true);
      setOverflowedBtnsCount((prevValue: number) => {
        if (overflowedBtnsCount === filteredAvailableButtons.length) {
          return prevValue;
        } else return prevValue + 1;
      });
    }

    const isEnoughtSpace = JSON.parse(IS_VISIBLE_SIDEBAR as string)
      ? spaceToLeftSideOfFilters -
          sidebarWidth -
          allButtonsWidth +
          groupBtnWidth >
        minEmptyWidthForNewBtn
      : spaceToLeftSideOfFilters - allButtonsWidth + groupBtnWidth >
        minEmptyWidthForNewBtn;

    if (isEnoughtSpace) {
      setOverflowedBtnsCount((prevValue: number) => {
        if (prevValue <= 1) {
          setIsOpenGroupBtn(false);
          return 0;
        } else return prevValue - 1;
      });
    }
  }, [filteredAvailableButtons, overflowedBtnsCount, sidebarWidth, IS_VISIBLE_SIDEBAR]);

  useEffect(() => {
    handleWindowResize();

    window.addEventListener("resize", handleWindowResize);

    return () => {
      window.removeEventListener("resize", handleWindowResize);
    };
  }, [handleWindowResize]);

  const handleDragEnd = (result: DropResult) => {
    if (!result.destination) return;
    const optionsToUpdate: any[] = Array.from(desktopListOrder);
    const [movedOption] = optionsToUpdate.splice(result.source.index, 1);
    optionsToUpdate.splice(result.destination.index, 0, movedOption);
    setDesktopListOrder(optionsToUpdate);
    dispatch(reducerEditOrderDesktop(optionsToUpdate));
    dispatch(actionAddDesktop());
  };

  useEffect(() => {
    if (location.pathname === "/charts" && desktopList?.length > 0) {
      if (index > 0) {
        navigate(`/charts/${desktopList[index].id}`);
      } else {
        navigate(`/charts/${desktopList[0].id}`);
      }
    }
  }, [location.pathname, desktopList, navigate, index]);

  useEffect(() => {
    setDesktopListOrder(desktopList);
    // eslint-disable-next-line
  }, [desktopList]);

  useEffect(() => {
    dispatch(actionGetDesktop("customerReport")).then((data: any) => {
      if (!reviewStatus && data?.desktopList?.length > 0) {
        const newList = data.desktopList.map((desktop: any) => {
          return {
            ...desktop,
            list: desktop.list.filter(
              (item: { id: number; type: string; isCopy: boolean }) => {
                return item.type !== "checkStatus";
              }
            ),
          };
        });
        dispatch(reducerEditOrderDesktop(newList));
        dispatch(actionAddDesktop());
      }
    });
    // eslint-disable-next-line
  }, []);

  return (
    <DragDropContext onDragEnd={handleDragEnd}>
      <div className="App">
        <Sidebar />{" "}
        <SectionContent
          availableButtons={displayedAvailableBtns}
          headBlockChildren={displayedHeadBtns}
          buttonsContainerRef={buttonsContainerRef}
          isOpenGroupBtn={isOpenGroupBtn}
          pageName={desktopList?.length ? "isCharts" : "unloadings"}
          section="Аналитика"
          title={findItem?.name || ""}
        >
          <div
            className={clsx(
              JSON.parse(IS_VISIBLE_SIDEBAR as string) &&
                isOpenSidebar &&
                "active__content",
              !isOpenSidebar && "closed__content",
              "content"
            )}
          >
            <Droppable droppableId="options" direction="horizontal">
              {(provided) => (
                <div
                  {...provided.droppableProps}
                  ref={provided.innerRef}
                  className="btns"
                >
                  {findItem &&
                    desktopList.length > 0 &&
                    desktopListOrder.map((el: any, index: number) => {
                      return (
                        <Draggable
                          draggableId={index.toString()}
                          index={index}
                          key={index}
                        >
                          {(provided) => (
                            <Link
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              {...provided.dragHandleProps}
                              to={`/charts/${el.id}`}
                              className="tab"
                              key={index}
                            >
                              <div
                                className={`buttons__btn ${
                                  (!id && el.id === 0) ||
                                  (id && el?.id >= 0 && id === el.id.toString())
                                    ? "--active"
                                    : ""
                                }`}
                              >
                                {el.name}
                              </div>
                            </Link>
                          )}
                        </Draggable>
                      );
                    })}{" "}
                  {
                    <HeaderBtn
                      content="+"
                      purpose="addDesktop"
                      modalTitle="Добавить рабочий стол"
                    />
                  }
                </div>
              )}
            </Droppable>{" "}
            {findItem && (
              <DndProvider backend={HTML5Backend}>
                {" "}
                <CustomerCharts data={findItem.list} />{" "}
              </DndProvider>
            )}
          </div>
        </SectionContent>
      </div>
    </DragDropContext>
  );
};

export default ManagementChartsSection;
